import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ChevronDown, FileText, Lock, LockOpen, RotateCcw, Sparkles } from "lucide-react";

import type { DocumentSection, FactSheet, Validation } from "@/api/schemas";
import { exportDocuments, triggerDownload } from "@/api/client";
import { Button } from "@/components/ui/button";
import { BadgeStatus } from "@/components/ui/badge-status";
import { CopyButton } from "@/components/ui/copy-button";
import { SourceChip } from "@/components/ui/source-chip";
import { cn } from "@/lib/utils";
import {
  contentClassBadge,
  ifcGroundedFactKeys,
  perSectionStatus,
  sourceTagClass,
  sourceTagLabel,
} from "./status";
import {
  placeholderInfo,
  placeholderLabel,
  placeholderTooltip,
} from "./placeholderFlags";
import { RefineSheet } from "./RefineSheet";
import { NumericChips } from "./NumericChip";
import { RegenerateDiffSheet } from "./RegenerateDiffSheet";
import { IzjavaPdfPreview } from "./IzjavaPdfPreview";
import { IzjavaMissingBar } from "./IzjavaMissingBar";
import { officePayload, sprancaRequestValue, useOfficeStore } from "@/features/projects/useOfficeStore";
import { useDocgenStore } from "./useDocgenStore";

interface DocSectionCardProps {
  section: DocumentSection;
  validation: Validation;
  factSheet: FactSheet;
  /** Merged opći-dio variables — needed to render the izjava office PDF. */
  opciVariables?: Record<string, unknown>;
  defaultOpen?: boolean;
}

/**
 * Single collapsible section. Replaces the legacy ``.doc-section``
 * markup. Owns the regenerate mutation, export, copy, and refine
 * panel — but the actual content rendering stays a plain ``<pre>``
 * here so Sprint B's NumericChip can swap it in cleanly.
 */
export function DocSectionCard({
  section,
  validation,
  factSheet,
  opciVariables,
  defaultOpen = false,
}: DocSectionCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [refineOpen, setRefineOpen] = useState(false);
  // Sprint B — W5. The regenerate flow now opens a diff sheet
  // (RegenerateDiffSheet) that owns its own mutation; we just track
  // open/close + the proposed content the user sent to "Spoji ručno"
  // so the RefineSheet can pre-load it.
  const [diffOpen, setDiffOpen] = useState(false);
  const [refineProposed, setRefineProposed] = useState<string | null>(null);

  const status = perSectionStatus(section.section_id, validation);
  const isLlm = section.source === "llm" || section.source === "llm_refined";
  const isPlaceholder = section.source === "placeholder";
  const isIzjava = section.section_type === "izjava";
  const ifcKeys = ifcGroundedFactKeys(section, factSheet);
  // F3 — unfinished-content flag ([ ZA RUČNI UNOS ] / plan nije u bazi):
  // same detection that marks the row in the Mapa 1 Sadržaj above.
  const unfinished = placeholderInfo(section);

  // Sprint B — W6. Consume keyboard-shortcut actions targeting this
  // section. The store clears the queue on consumption so a second
  // press of the same shortcut re-triggers (rather than no-opping).
  const focusedSectionId = useDocgenStore((s) => s.focusedSectionId);
  const pendingAction = useDocgenStore((s) => s.pendingAction);
  const clearPendingAction = useDocgenStore((s) => s.clearPendingAction);
  const patchSection = useDocgenStore((s) => s.patchSection);
  const isFocused = focusedSectionId === section.section_id;
  const locked = Boolean(section.locked);

  useEffect(() => {
    if (!pendingAction || pendingAction.sectionId !== section.section_id) return;
    if (pendingAction.type === "toggle") {
      setOpen((v) => !v);
    } else if (pendingAction.type === "regenerate" && isLlm) {
      setOpen(true);
      setDiffOpen(true);
    } else if (pendingAction.type === "refine" && (isLlm || isPlaceholder)) {
      setOpen(true);
      setRefineProposed(null);
      setRefineOpen(true);
    }
    clearPendingAction(section.section_id);
  }, [pendingAction, section.section_id, isLlm, isPlaceholder, clearPendingAction]);

  const exportOne = useMutation({
    mutationFn: async (format: "docx" | "pdf") => {
      const blob = await exportDocuments({
        format,
        sections: [section],
        fact_sheet: factSheet,
        tab_name: section.title,
        variables: opciVariables,
        office: officePayload(useOfficeStore.getState().office) ?? undefined,
        spranca: sprancaRequestValue() ?? undefined,
      });
      const ext = format === "docx" ? "docx" : "pdf";
      triggerDownload(blob, `Permitko_${section.section_id}.${ext}`);
    },
  });

  return (
    <article
      id={`ds-${section.section_id}`}
      data-section-id={section.section_id}
      className={cn(
        "mb-3 scroll-mt-[120px] rounded-lg border bg-white shadow-sm transition-all",
        isPlaceholder ? "border-orange-200" : "border-stone-200",
        isLlm && "border-l-[3px] border-l-ind-500",
        // Sprint B — W6. Visual marker for the keyboard-focused card
        // so j/k navigation has a 1-glance "you are here" indicator.
        isFocused && "ring-2 ring-ind-300",
      )}
    >
      <header
        className="flex cursor-pointer items-center gap-2 px-4 py-3 transition-colors hover:bg-stone-50"
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className={cn(
            "inline-flex flex-shrink-0 items-center rounded border px-1.5 py-0.5 text-[0.7rem] font-bold uppercase tracking-[0.04em]",
            sourceTagClass(section.source),
          )}
        >
          {sourceTagLabel(section.source)}
        </span>
        {status.kind !== "ok" && (
          <BadgeStatus kind={status.kind} title={status.tooltip}>
            {status.label}
          </BadgeStatus>
        )}
        {unfinished && (
          <BadgeStatus kind="warn" title={placeholderTooltip(unfinished)}>
            {placeholderLabel(unfinished)}
          </BadgeStatus>
        )}
        {(() => {
          // S3 — klasa B: amber "generirano — pregledaj" oznaka.
          const cc = contentClassBadge(section);
          return cc ? (
            <BadgeStatus kind="warn" title={cc.tooltip}>
              {cc.label}
            </BadgeStatus>
          ) : null;
        })()}
        <h3 className="flex-1 truncate text-[0.92rem] font-semibold text-stone-800">
          {section.title}
        </h3>

        <div
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
          role="toolbar"
        >
          <CopyButton text={section.content} label="Kopiraj" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => patchSection(section.section_id, { locked: !locked })}
            title={
              locked
                ? "Otključaj — dopusti da ažuriranje regenerira ovu sekciju"
                : "Zaključaj — zaštiti od ažuriranja kad se promijene podaci"
            }
          >
            {locked ? (
              <Lock className="h-3.5 w-3.5 text-amber-600" />
            ) : (
              <LockOpen className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => exportOne.mutate("docx")}
            title="Preuzmi kao Word"
            disabled={exportOne.isPending}
          >
            <FileText className="h-3.5 w-3.5" />
          </Button>
          {isLlm && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDiffOpen(true)}
              title="Regeneriraj iz AI-a (s pregledom razlika)"
              disabled={diffOpen}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          )}
          {(isLlm || isPlaceholder) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setRefineProposed(null);
                setRefineOpen(true);
              }}
              title="AI dorada"
            >
              <Sparkles className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        <ChevronDown
          className={cn(
            "ml-1 h-4 w-4 flex-shrink-0 text-stone-400 transition-transform",
            open && "rotate-180",
          )}
        />
      </header>
      {open && (
        <div className="border-t border-stone-100 px-4 py-4">
          {ifcKeys.length > 0 && (
            <div className="-mx-4 mb-2">
              <SourceChip factKeys={ifcKeys} />
            </div>
          )}
          {isIzjava ? (
            // True WYSIWYG: the office-fidelity PDF, not flattened text.
            <>
              {(section.missing?.length ?? 0) > 0 && (
                <IzjavaMissingBar missing={section.missing ?? []} />
              )}
              <IzjavaPdfPreview
                slot={section.section_id}
                factSheet={factSheet}
                variables={opciVariables}
                fallbackText={section.content}
              />
            </>
          ) : (
          <pre
            className={cn(
              "whitespace-pre-wrap rounded-md bg-stone-50 p-4 font-sans text-[0.88rem] leading-relaxed text-stone-700",
              section.section_type === "table" && "font-mono text-[0.78rem]",
            )}
          >
            {/* Sprint B — W4. NumericChips wraps every fact-matched
                numeric in a Tooltip surfacing the source fact key +
                IFC GlobalIds. Falls back to plain text on table /
                placeholder sections (no LLM-emitted numerics there
                that'd benefit from the tooltip). */}
            {isLlm ? (
              <NumericChips content={section.content} factSheet={factSheet} />
            ) : (
              section.content
            )}
          </pre>
          )}
        </div>
      )}

      {refineOpen && (
        <RefineSheet
          open={refineOpen}
          onOpenChange={(next) => {
            setRefineOpen(next);
            if (!next) setRefineProposed(null);
          }}
          section={section}
          factSheet={factSheet}
          proposedContent={refineProposed}
        />
      )}

      {diffOpen && isLlm && (
        <RegenerateDiffSheet
          open={diffOpen}
          onOpenChange={setDiffOpen}
          section={section}
          factSheet={factSheet}
          onMergeManually={(proposed) => {
            // Bridge: diff sheet hands off the AI proposal here, we
            // pre-stage it on the refine sheet and open it.
            setRefineProposed(proposed);
            setRefineOpen(true);
          }}
        />
      )}
    </article>
  );
}
